import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { gql, useQuery } from '@apollo/client'

import CartItemsComponent from '../../../../components/customers/cart/cartItems'

import { CART_ITEM_FRAGMENT, useAddItemsToCartMutation } from './useAddItemsToCartMutation'
import { useRemoveCartItemMutation } from './useRemoveCartItemMutation'

const GET_CUSTOMER_CART_ITEMS_QUERY = gql`
  ${CART_ITEM_FRAGMENT}
  query GetCustomerCartItems($customerId: String!) {
    customerCart(customerNo: $customerId) {
      id
      itemsTotalPrice {
        display
      }
      subItemsTotalPrice {
        display
      }
      itemsTotalWeight {
        display
      }
      items {
        ...CartItem
      }
      onlyDeletedItems {
        ...CartItem
      }
    }
  }
`

const dataParser = customerCart => {
  if (!customerCart) return null

  const {
    itemsTotalPrice,
    subItemsTotalPrice,
    itemsTotalWeight,
    items = [],
    onlyDeletedItems = [],
    shippingAddress,
    ...source
  } = customerCart || {}

  return {
    ...source,
    // Combine regular items and deleted items
    items: [...items, ...onlyDeletedItems] || [],
    summary: {
      itemsTotalWeight: itemsTotalWeight ? itemsTotalWeight.display : '0',
      itemsTotalPrice: itemsTotalPrice ? itemsTotalPrice.display : '0',
      subItemsTotalPrice: subItemsTotalPrice ? subItemsTotalPrice.display : '0'
    }
  }
}

const CartItemsContainer = ({ customerId }) => {
  const [editor, setEditor] = useState({})
  const [externalId, setExternalId] = useState('')
  const [quantity, setQuantity] = useState(1)

  const { data, loading, error } = useQuery(GET_CUSTOMER_CART_ITEMS_QUERY, {
    variables: { customerId }
  })

  const { addItemsToCart } = useAddItemsToCartMutation()
  const { removeCartItems } = useRemoveCartItemMutation()

  if (error) { console.error(error) }

  const { items, summary } = data ? dataParser(data.customerCart) : {}

  const onAddItemToCart = async event => {
    event.preventDefault()

    await onAddItemsToCart([{
      itemId: externalId,
      quantity: quantity
    }])

    setExternalId('')
    setQuantity(1)
  }

  const onAddItemsToCart = async cartItems => {
    await addItemsToCart({
      variables: { customerNo: customerId, cartItems }
    })
  }

  const onChangeEditorInput = value => {
    setEditor(prev => ({ ...prev, value: value }))
  } 

  const onCloseEditor = () => {
    setEditor({})
  }

  const onOpenEditor = ({ name, record }) => {
    setEditor({ name, current: record.id })
  }

  const onRemoveCartItems = async cartItemIds => {
    await removeCartItems({
      variables: {
        customerNo: customerId,
        cartItemIds: cartItemIds
      }
    })
  }

  const onUpdateQuantity = async record => {
    await onAddItemsToCart([{
      itemId: record.item.id,
      cartItemId: record.id,
      quantity: editor.value || record.quantity
    }])

    onCloseEditor()
  }

  return (
    <CartItemsComponent
      dataSource={items}
      loading={loading}
      onAddItemToCart={onAddItemToCart}
      onRemoveCartItems={onRemoveCartItems}
      onUpdateQuantity={onUpdateQuantity}
      summary={summary}
      editorState={{
        ...editor,
        change: onChangeEditorInput,
        close: onCloseEditor,
        open: onOpenEditor
      }}
      externalIDState={{
        value: externalId,
        onChange: event => setExternalId(event.target.value)
      }}
      quantityState={{
        value: quantity,
        onChange: value => setQuantity(value)
      }} />
  )
}

CartItemsContainer.propTypes = {
  customerId: PropTypes.string.isRequired
}

export default CartItemsContainer
